import React, { useState } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import { useTranslation } from "react-i18next";
import { useGuardedNavigate } from "../../contexts/NavigationGuardContext";
import useUrlDialog from "../../Hooks/useUrlDialog";
import UrlDialog from "../../components/dialog/UrlDialog";
import { useDisplayMode } from "../context/DisplayModeContext";
import { useServerYear } from "../../Hooks/useServerYear";
import { useVisitCounter } from "../hooks/useVisitCounter";
import phoneIcon from "../../assets/rewamp/footer/phone_icon.svg";
import emailIcon from "../../assets/rewamp/footer/email_icon.svg";

const getRewampFooterLinks = () => [
  {
    header: "ImportantLinks",
    list: [
      {
        title: "DFSWEB_ASSETS",
        path: `${window.contextPath}/#assets`,
      },
      {
        title: "DFSWEB_SCHEMES",
        path: `${window.contextPath}/#schemes`,
      },
      {
        title: "DFSWEB_HELP",
        path: `${window.contextPath}/#help-support`,
      },
      {
        title: "Privacy_policy",
        path: `${window.contextPath}/privacypolicy`,
      },
      {
        title: "Accessibility_statement",
        path: `${window.contextPath}/accessibility-statement`,
      },
      {
        title: "Sitemap",
        path: `${window.contextPath}/sitemap`,
      },
    ],
  },
  {
    header: "OtherImportantLinks",
    list: [
      { title: "DBTPortal", link: "https://dbtagriculture.bihar.gov.in/" },
      { title: "BiharSeedCertificationAgency", link: "https://bssca.co.in/" },
      { title: "DirectorateofHorticulture", link: "https://horticulture.bihar.gov.in/" },
      {
        title: "KisanCallCentrePortal",
        link: "https://mkisan.gov.in/Alpha/aboutkcc.aspx",
      },
      { title: "BAVAS", link: "https://bavas.bihar.gov.in/" },
      { title: "BWDS", link: "https://bwds.bihar.gov.in/Home/Index" },
      { title: "DEPARTMENT_REVENUE_LAND_REFORM", link: "https://biharbhumi.bihar.gov.in/Biharbhumi/" },
      { title: "PM_KISHAN", link: "https://www.pmkisan.gov.in/" },
      { title: "BRBN", link: "https://brbn.bihar.gov.in/" },
      { title: "BAMETI", link: "https://www.bameti.org/"},
    ],
  },
  {
    header: "ContactInformation",
    list: [
      {
        title: "BiharKrishiSupport",
        contact: "support.biharkrishi@bihar.gov.in",
        icon: "EmailIcon",
      },
      {
        title: "REWAMP_KISAAN_HELPLINE",
        contact: "1800-180-1551",
        icon: "PhoneIcon",
      },
    ],
  },
];

const ImageHoverEffect = ({ defaultSrc, hoverSrc, altText }) => {
  const [src, setSrc] = useState(defaultSrc);

  return (
    <Box
      component="img"
      src={src}
      alt={altText}
      onMouseEnter={() => setSrc(hoverSrc)}
      onMouseLeave={() => setSrc(defaultSrc)}
      sx={{
        transition: "all 0.5s ease-in-out",
        height: "28px",
        width: "28px",
        objectFit: "contain",
      }}
    />
  );
};

export const RewampFooter = React.memo(({ initData }) => {
  const { t } = useTranslation();
  const copyrightYear = useServerYear();
  const totalVisits = useVisitCounter();
  const { standalone } = useDisplayMode();
  // Indian-style grouping with ASCII digits, consistent with the other
  // numerals in this footer (year, release date). "-" until resolved.
  const formattedVisits =
    typeof totalVisits === "number" ? totalVisits.toLocaleString("en-IN") : "-";
  const { open, externalUrl, handleLinkClick, handleClose } = useUrlDialog();
  const navigate = useGuardedNavigate();
  const footerLinks = getRewampFooterLinks();

  const handleMenuClick = (path, link) => (e) => {
    e.preventDefault();
    if (path != null) {
      navigate(path);
    } else if (link != null) {
      handleLinkClick(link, e);
    }
  };

  const socialMedia = [
    {
      src: "/assets/rewamp/youtube_icon.svg",
      srcH: "/assets/rewamp/youtube_icon.svg",
      alt: t("YOUTUBE_ICON"),
      link: "https://www.youtube.com/@BiharKrishiApp",
    },
    {
      src: "/assets/rewamp/facebook_icon.svg",
      srcH: "/assets/rewamp/facebook_icon.svg",
      alt: t("FACEBOOK_ICON"),
      link: "https://www.facebook.com/BiharKrishiApp/",
    },
    {
      src: "/assets/rewamp/x_icon.svg",
      srcH: "/assets/rewamp/x_icon.svg",
      alt: t("X_ICON"),
      link: "https://x.com/AppKrishi",
    },
    {
      src: "/assets/rewamp/instagram_icon.svg",
      srcH: "/assets/rewamp/instagram_icon.svg",
      alt: t("INSTAGRAM_ICON"),
      link: "https://www.instagram.com/biharkrishidfs/?utm_source=ig_web_button_share_sheet",
    },
  ];

  if (standalone) {
    return null;
  }

  return (
    <Box
      component="footer"
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        // Never shrink when the layout flex-column pushes us to the bottom.
        flexShrink: 0,
        bgcolor: "primary.main",
        pt: { xs: 4, md: 6 },
        pb: {xs: 1, md: 2.5},
        gap: 4,
      }}
    >
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: {xs: 2, md: 3}
        }}
      >
        <Box
          sx={{
            width: "100%",
            px: { xs: 2, sm: 5, md: 4 },
          }}
        >
          <Grid container spacing={4} justifyContent="space-between">
            {/* Brand Section */}
            <Grid item xs={12} md={3}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 2 }}>
                {/* Logo & Name */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Box
                    component="img"
                    src={`${process.env.PUBLIC_URL}/assets/rewamp/bihar_krishi_logo-2c4dff.png`}
                    alt={t("BIHAR_KRISHI_LOGO")}
                    sx={{
                      width: "59px",
                      height: "42px",
                      objectFit: "contain",
                      filter: "drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25))",
                    }}
                  />
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography
                      variant="h3"
                      component="p"
                      sx={{ color: "background.white" }}
                    >
                      {t("BIHAR_KRISHI_APP")}
                    </Typography>
                    <Typography
                      variant="body2"
                      component="p"
                      sx={{ color: "background.white" }}
                    >
                      {t("DIGITAL_FARMER_SERVICES")}
                    </Typography>
                  </Box>
                </Box>

                {/* Description */}
                <Typography
                  variant="body1"
                  sx={{
                    color: "background.white",
                  }}
                >
                  {t("GOVT_INITIATIVE_DESC")}
                </Typography>

                {/* Follow Us */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <Typography
                    variant="h4"
                    component="p"
                    sx={{ color: "background.white" }}
                  >
                    {t("FOLLOW_US")}:
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                    {socialMedia.map((social, idx) => (
                      <Link
                        key={idx}
                        href={social.link}
                        onClick={(e) => {
                          e.preventDefault();
                          handleLinkClick(social.link, e);
                        }}
                        rel="noopener noreferrer"
                        aria-label={social.alt}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: "6px",
                          "&:focus-visible": {
                            outline: "2px solid white",
                            outlineOffset: "2px",
                          },
                        }}
                      >
                        <ImageHoverEffect
                          defaultSrc={`${window.contextPath}${social.src}`}
                          hoverSrc={`${window.contextPath}${social.srcH}`}
                          altText={social.alt}
                        />
                      </Link>
                    ))}
                  </Box>
                </Box>
              </Box>
            </Grid>

            {/* Render Links Columns based on footerLinks */}
            {footerLinks?.map((linkGroup, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
                  <Typography
                    variant="h4"
                    component="h3"
                    sx={{ color: "background.white" }}
                  >
                    {t(linkGroup.header)}
                  </Typography>

                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {linkGroup.list.map((item, idx) => (
                      <Box key={item.title}>
                        <Link
                          href={item?.path || item?.link || "#"}
                          variant="body1"
                          underline="none"
                          sx={{
                            py: "5px",
                            textAlign: "left",
                            color: "background.white",
                            minHeight: "24px",
                            "&:hover": {
                              // Footer sits on primary.main (green); the global
                              // MuiLink hover color (primary.dark) would blend
                              // into that background. Keep the link white — the
                              // underline is the hover affordance here.
                              color: "background.white",
                              textDecoration: "underline",
                            },
                            "&:focus-visible": {
                              outline: "2px solid white",
                              outlineOffset: "2px",
                            },
                          }}
                          onClick={handleMenuClick(item?.path, item?.link)}
                        >
                          {t(item.title)}
                        </Link>

                        {/* Contact details like Phone or Email if present */}
                        {item?.contact && (
                          <Tooltip title={t(item.contact)}>
                            <Box sx={{ display: "flex", alignItems: "center", mt: 0.5 }}>
                              {item?.icon === "PhoneIcon" ? (
                                <Box
                                  component="img"
                                  src={phoneIcon}
                                  alt=""
                                  aria-hidden="true"
                                  sx={{ width: "18px", height: "18px", mr: 1, objectFit: "contain" }}
                                />
                              ) : item?.icon === "EmailIcon" ? (
                                <Box
                                  component="img"
                                  src={emailIcon}
                                  alt=""
                                  aria-hidden="true"
                                  sx={{ width: "18px", height: "18px", mr: 1, objectFit: "contain" }}
                                />
                              ) : null}
                              <Typography
                                variant="body1"
                                component="a"
                                href={`${item?.icon === "PhoneIcon" ? "tel:" : "mailto:"}${item?.contact}`}
                                sx={{
                                  color: "background.white",
                                  textDecoration: "none",
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                {t(item.contact)}
                              </Typography>
                            </Box>
                          </Tooltip>
                        )}
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Bottom Copyright Bar */}
        <Box
          sx={{
            width: "100%",
            borderTop: 1,
            borderColor: "onGreen.divider",
            px: { xs: 2, sm: 5, md: 4 },
            py: 1
          }}
        >
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              <Typography
                variant="body2"
                sx={{
                  color: "background.white",
                  textAlign: { xs: "center", md: "left" },
                }}
              >
                © {copyrightYear} {t("DepartofAgri")}, {t("govtOfBihar")}. {t("ALL_RIGHTS_RESERVED")}
              </Typography>

              <Box sx={{ display: "flex", alignItems: "center", justifyContent: { xs: "center", md: "flex-start" }, gap: 1 }}>
                <Typography
                  variant="body2"
                  sx={{ color: "background.white" }}
                >
                  {t("lastUpdate")} : {initData?.releaseInfo?.webApp?.["release-date"] ?? "N/A"}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "background.white" }}
                >
                  |
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "background.white" }}
                >
                  {t("TOTAL_VISITS")}: {formattedVisits}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                variant="body2"
                sx={{ color: "background.white", lineHeight: 1 }}
              >
                {t("WEBSITE_DEVELOPED_BY")}
              </Typography>
              <Link
                href="https://www.microsave.net/"
                onClick={(e) => {
                  e.preventDefault();
                  handleLinkClick("https://www.microsave.net/");
                }}
                rel="noopener noreferrer"
                aria-label={t("MSC_LOGO")}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  borderRadius: "6px",
                  "&:focus-visible": {
                    outline: "2px solid white",
                    outlineOffset: "2px",
                  },
                }}
              >
                <Box
                  component="img"
                  src={`${process.env.PUBLIC_URL}/assets/rewamp/msc_logo-56586a.png`}
                  alt={t("MSC_LOGO")}
                  sx={{ height: "40px", width: "auto", cursor: "pointer" }}
                />
              </Link>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* External URL Dialog */}
      <UrlDialog
        open={open}
        externalUrl={externalUrl}
        handleClose={handleClose}
        t={t}
      />
    </Box>
  );
});

export default RewampFooter;
